export interface IPlanningSmbGuidedTranslation {
  key: string
  fallback: string
}

const text = (suffix: string, fallback: string): IPlanningSmbGuidedTranslation => ({
  key: `jbp-planning-smb-guided-wizard-${suffix}`,
  fallback,
})

/** English defaults keep new keys readable until the translation dictionary is published. */
export const SMB_GUIDED_WIZARD_TEXT = {
  title: text('title', 'SMB vs Enterprise planning'),
  // Preserve the published three-year keys; shorter plans use a real period/end-year parameter.
  completedPeriod: text('financial-plan-complete-period', '{planPeriod} financial plan complete'),
  startLeadPeriod: text(
    'start-lead-period',
    'It takes one number. Check your FY{baselineYear} split, set how much SMB should grow each year — the plan for {planPeriod} fills in automatically.'
  ),
  setGrowthPeriod: text('set-growth-period', 'Set one SMB growth number for {planPeriod}'),
  shareTitlePeriod: text(
    'share-title-period',
    '{programType} SMB share, FY{baselineYear} → FY{lastPlanYear}'
  ),
  growthResultPeriod: text(
    'growth-result-period',
    'SMB grows from {baseline}% to {planned}% by FY{lastPlanYear}.'
  ),
  growthResultNoBaselinePeriod: text(
    'growth-result-no-baseline-period',
    'SMB reaches {planned}% by FY{lastPlanYear}.'
  ),
  context: text('context', '{partnerName} · {programType} · FY{fYear}'),
  fiscalYear: text('fiscal-year', 'FY{year}'),
  completed: text('financial-plan-complete', 'FY{fYear}–FY{fYearNextTwo} financial plan complete'),
  startTitle: text('start-title', 'Plan your SMB vs Enterprise split'),
  startLead: text(
    'start-lead',
    'It takes one number. Check your FY{baselineYear} split, set how much SMB should grow each year — FY{fYear}–FY{fYearNextTwo} fill in automatically.'
  ),
  required: text('all-you-need', 'All you need'),
  reviewBaseline: text('review-baseline', 'Review your FY{baselineYear} SMB vs Enterprise split'),
  setGrowth: text('set-growth', 'Set one SMB growth number for FY{fYear}–FY{fYearNextTwo}'),
  optional: text('optional', 'Optional'),
  optionalLead: text(
    'optional-lead',
    'Plan precisely by sub-category or by quarter — only if you know they differ'
  ),
  notNow: text('not-now', 'Not now'),
  start: text('start-planning', 'Start planning'),
  shareTitle: text('share-title', '{programType} SMB share, FY{baselineYear} → FY{fYearNextTwo}'),
  shareNote: text(
    'share-note',
    'FY{baselineYear} is your actual split. The plan years update as you change the growth number.'
  ),
  smb: text('smb', 'SMB'),
  enterprise: text('enterprise', 'Enterprise'),
  enterpriseLegend: text('enterprise-legend', 'Enterprise — the rest, fills automatically'),
  actual: text('actual', 'Actual'),
  plan: text('plan', 'Plan'),
  growth: text('growth-per-year', 'SMB growth per year'),
  growthHint: text(
    'growth-hint',
    'Percentage points added to SMB share each year, on top of FY{baselineYear}.'
  ),
  growthResult: text(
    'growth-result',
    'SMB grows from {baseline}% to {planned}% by FY{fYearNextTwo}.'
  ),
  growthResultNoBaseline: text(
    'growth-result-no-baseline',
    'SMB reaches {planned}% by FY{fYearNextTwo}.'
  ),
  enough: text(
    'enough',
    "This is enough to complete the plan. It's applied to all {count} sub-categories and every quarter."
  ),
  fillsUnchanged: text('fills-unchanged', "Fills every value you don't change"),
  points: text('percentage-points', 'pp'),
  decrease: text('decrease-growth', 'Decrease SMB growth'),
  increase: text('increase-growth', 'Increase SMB growth'),
  more: text('plan-in-detail', 'Want more detail? Plan by sub-category and quarter →'),
  back: text('back-to-overview', '← Back to one-number view'),
  cancel: text('cancel', 'Cancel'),
  save: text('save-plan', 'Save plan'),
  saving: text('saving', 'Saving…'),
  financial: text('show-financial-data', 'Show financial data'),
  metric: text('financial-metric', 'Financial value type'),
  currency: text('currency', 'Currency'),
  scale: text('number-scale', 'Number scale'),
  asIs: text('as-is', 'As is'),
  product: text('product', 'Product'),
  yearPlan: text('year-plan', 'FY{year} plan'),
  smbPercent: text('smb-percent', 'SMB %'),
  byQuarter: text('by-quarter', 'FY{fYear} by quarter'),
  quarters: text('quarters', 'Quarters'),
  collapse: text('collapse-quarters', '◂ Collapse'),
  quarter: text('quarter', 'Q{quarter}'),
  subCategories: text('sub-categories', '{count} sub-categories'),
  follows: text('follows-growth', 'All follow {programType} growth'),
  adjusted: text('adjusted-categories', '{adjusted} adjusted · {following} follow {programType}'),
  adjust: text('adjust-individually', 'Adjust individually'),
  hide: text('hide-categories', 'Hide'),
  inheritedLegend: text('inherited-legend', 'Grey = follows {programType} growth'),
  customLegend: text('custom-legend', 'Blue = your custom number'),
  detailNote: text(
    'detail-note',
    'Only change a sub-category if you expect a different SMB share there.'
  ),
  total: text('total', 'Total'),
  reset: text('reset', 'Reset to growth number'),
  percentLabel: text('percent-input-label', '{product}, FY{year}{quarter}: SMB %'),
  expandProduct: text('expand-product', 'Show details for {product}'),
  collapseProduct: text('collapse-product', 'Hide details for {product}'),
  noBaseline: text(
    'no-baseline',
    'No SMB vs Enterprise split is available for FY{baselineYear}. You can still plan; the starting share uses the existing plan, or 50% when no split has been entered.'
  ),
  clamped: text('clamped', 'SMB share is kept between 0% and 100%.'),
  overwriteTitle: text('overwrite-title', 'Overwrite existing split data?'),
  overwriteBody: text(
    'overwrite-body',
    'This will replace the existing split data for {programType} in FY{fYear}. Continue?'
  ),
  overwriteApply: text('overwrite-apply', 'Overwrite'),
  overwriteKeep: text('overwrite-keep', 'Keep current year'),
} as const

/** Period-aware copy for the optional legacy flow; its published three-year copy is unchanged. */
export const SMB_GUIDED_LEGACY_PERIOD_TEXT = {
  congratulationsTitle: text(
    'legacy-congratulations-title-period',
    'Nice work finishing the {planPeriod} financial plan for {programType}!'
  ),
  congratulationsBody: text(
    'legacy-congratulations-body-period',
    'This year, HP is putting a specific focus on growing the SMB business across the portfolio. We have prepared your FY{baselineYear} SMB vs EU baseline to review and set an aspirational SMB growth for {planPeriod}. Do you want to proceed now?'
  ),
  reviewNote: text(
    'legacy-review-note-period',
    'Here is the FY{baselineYear} (Actuals) SMB vs EU baseline for {programType}. Enter one aspirational YoY growth number, in percentage points of SMB — it applies the same way to {planPeriod} (each measured independently vs. this FY{baselineYear} baseline). EU adjusts automatically.'
  ),
} as const
